import { mapState } from 'pinia';
import stores from 'store/modules';
import filters from '../vue/filters';

export default {
    data() {
        return {
            error: null,
            errorText: '',
            loading: null,
            tagSettings: {
                valueField: 'id',
                searchField: ['label'],
                options: [],
                create: this.createTag,
                highlight: false,
                render: {
                    item: this.tagItem,
                    option: this.tagOption,
                },
                load: this.searchTags,
                onType: this.searchTagsOnType,
                showCounts: false,
                plugins: ['remove_button'],
            },
            tagResults: {
                force: false,
                value: '',
                cached: {},
                data: null,
            },
        };
    },

    computed: {
        /**
         * Returns boolean of whether the user has any tags
         *
         * @return {Boolean}
         */
        hasTags() {
            return this.tags && this.tags.length > 0;
        },

        /**
         * Placeholder for selectize tags dropdown.
         *
         * @returns {String}
         */
        placeholder() {
            return this.hasTags ? 'Type to select tags..' : 'Please create some tags first..';
        },

        /**
         * Returns true if there are no results for the given query..
         *
         * @returns {Boolean}
         */
        noTagsResults() {
            if (this.tagResults.force) {
                return true;
            } else if (this.tagResults.value) {
                return (
                    this.tagResults.cached.hasOwnProperty(this.tagResults.value) &&
                    this.tagResults.cached[this.tagResults.value] === 0
                );
            } else {
                return false;
            }
        },

        ...mapState(stores.Tags, ['tags', 'tagsPagination']),
    },

    methods: {
        /**
         * Resets the search results object..
         *
         * @return {void}
         */
        resetTagResults() {
            this.tagResults = {
                force: false,
                value: '',
                cached: {},
                data: null,
            };
        },

        /**
         * Returns the tag item..
         *
         * @param {Object} item
         * @param {Object} escape
         * @returns {String}
         */
        tagItem(item, escape) {
            let escapedLabel = item.label.replace(/(<([^>]+)>)/gi, '');
            let tagItem = `<div class="selectize-contact tag is-small">${escapedLabel}`;
            if (this.tagSettings.showCounts) {
                tagItem += ` (${filters.number(item.contacts_count)})`;
            }
            tagItem += '</div>';

            return tagItem;
        },

        /**
         * Returns the tag option..
         *
         * @param {Object} item
         * @param {Object} escape
         * @returns {String}
         */
        tagOption(item, escape) {
            let escapedLabel = item.label.replace(/(<([^>]+)>)/gi, '');
            let tagOption = `<div class="selectize-contact">
                    <div class="selectize-contact-info">
                        <strong>${escapedLabel}`;

            if (this.tagSettings.showCounts) {
                tagOption += ` (${filters.number(item.contacts_count)})`;
            }

            tagOption += `</strong>
                    </div>
                </div>`;

            return tagOption;
        },

        /**
         * Creates a Tag since the user tried adding one via search bar..
         *
         * @param {String} input
         * @param {Function} callback
         * @return {Object}
         */
        createTag(input, callback) {
            let data = {
                label: input,
            };

            this.loading = true;
            this.error = false;
            this.errorText = '';
            this.resetTagResults();

            this.$store
                .dispatch('createTags', { useAxios: true, data: data })
                .then((response) => {
                    this.$store.dispatch('showToast', {
                        type: 'success',
                        message: 'Tag was created successfully!',
                    });

                    callback({
                        id: response.id,
                        label: response.label,
                    });
                })
                .catch(async (e) => {
                    const data = e?.response?.data;
                    const errorMessage =
                        data?.label && Array.isArray(data?.label) && data?.label[0];

                    this.error = true;
                    this.errorText = errorMessage || 'Something went wrong creating..';

                    callback();

                    if (this.dropdown) {
                        this.dropdown.refreshItems();
                        this.dropdown.refreshOptions();
                        this.dropdown.refreshState();
                        this.dropdown.setTextboxValue(input);

                        await this.$delay(100);

                        this.dropdown.close();
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * Sets the search value.
         *
         * @returns {void}
         */
        searchTagsOnType(value) {
            this.tagResults.force = false;
            this.tagResults.value = value;
        },

        /**
         * Search the tags backend..
         *
         * @param {String} query
         * @param {Function} callback
         * @returns {void}
         */

        searchTags(query, callback) {
            if (!query.length) {
                if (this.tags) {
                    callback(this.processResults(query, this.tags, true));
                } else {
                    callback();
                }

                return;
            }

            let options = {
                params: {
                    term: query,
                },
            };

            this.$store
                .dispatch('searchTags', { update: false, options: options })
                .then((response) => {
                    callback(this.processResults(query, response.data.data, false));
                })
                .catch(() => {
                    callback();
                });
        },

        /**
         * Handles everything related to bootstrapping search results.
         *
         * @param {String} query
         * @param {Object} results
         * @param {boolean} firstTime
         * @return {Object}
         */
        processResults(query, results, firstTime) {
            if (firstTime === undefined) {
                firstTime = false;
            }

            this.tagResults.data = results;

            let options = results.map((i) => {
                return {
                    id: i.id,
                    label: i.label,
                    name: i.name,
                    type: i.type,
                    contacts_count: i.contacts_count,
                };
            });

            this.tagResults.value = query;
            this.tagResults.cached[query] = options.length;

            if (!firstTime && !options.length) {
                this.tagResults.force = true;
            }

            return options;
        },

        /**
         * Remove given Tag from Contact..
         *
         * @param tag
         * @return {void}
         */
        async removeTag(tag) {
            try {
                this.loading = true;

                await this.$store.dispatch('deleteTagsByIdContactById', {
                    contactId: this.contact.id,
                    tagId: tag.id,
                });

                this.$store.dispatch('showToast', {
                    type: 'success',
                    message: 'Tag was removed from this Contact.',
                });
            } catch (e) {
                this.$log.error(e, filters__file, 'removeTag');

                this.$store.dispatch('tagDeleted', tag);
            } finally {
                this.loading = false;
            }

            this.loading = true;
        },
    },
};
